<template>
  <div class="bg-white">
    <div class="mb-6">
      <h2 class="text-xl font-medium text-black">Portales</h2>
    </div>
    
    <!-- Grid de portales -->
    <div v-if="Number(props.status_id) === 1 && allPartners && allPartners.length > 0">
      
      <!-- Primera fila: Portales de Tuin -->
      <div v-if="tuinPortals && tuinPortals.length > 0" class="mb-6">
        <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2">
          <div 
            v-for="partner in tuinPortals" 
            :key="partner.partner"
            class="flex items-start justify-start p-2"
          >
            <div class="flex flex-col items-start">
              <div class="flex justify-start">
                <!-- Logo del partner con tooltip -->
                <a 
                  :href="partner.url" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  :data-tooltip-target="`tooltip-${partner.partner}`"
                  data-tooltip-placement="bottom"
                >
                  <img 
                    :src="getPartnerLogo(partner.partner)" 
                    :alt="`Logo de ${partner.partner}`"
                    class="h-auto w-auto object-contain cursor-pointer"
                    @error="handleImageError"
                  />
                </a>
                
                <!-- Tooltip de Flowbite -->
                <div 
                  :id="`tooltip-${partner.partner}`" 
                  role="tooltip" 
                  class="absolute z-10 text-center invisible inline-block px-3 py-2 text-xs font-medium text-white bg-gray-900 rounded-lg shadow-sm opacity-0 tooltip dark:bg-gray-700"
                >
                  Da click para  <br> ver la propiedad
                  <div class="tooltip-arrow" data-popper-arrow></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Segunda fila: Otros portales con texto descriptivo -->
      <div v-if="otherPortals && otherPortals.length > 0">
        <template v-if="type_movement_id === 3">
          <!-- Fila para portales de venta -->
          <div v-if="salePortals && salePortals.length > 0" class="mb-6">
            <div class="mb-3">
              <p class="text-sm font-normal text-gray-900">
                Encuentra tu propiedad en <span class="font-semibold text-tuin-100">venta</span> en otras plataformas
              </p>
            </div>
            <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2">
              <div 
                v-for="partner in salePortals" 
                :key="`sale-${partner.partner}`"
                class="flex items-start justify-start p-2"
              >
                <div class="flex flex-col items-start">
                  <div class="flex justify-start">
                    <!-- Logo del partner con tooltip -->
                    <template v-if="partner.partner === 'Clasificados'">
                      <!-- Para Clasificados, usar div clickeable en lugar de <a> -->
                      <div 
                        @click="handlePartnerClick(partner, $event)"
                        :data-tooltip-target="`tooltip-${partner.partner}-sale`"
                        data-tooltip-placement="bottom"
                        class="cursor-pointer"
                      >
                        <img 
                          :src="getPartnerLogo(partner.partner)" 
                          :alt="`Logo de ${partner.partner}`"
                          class="h-auto w-auto object-contain cursor-pointer"
                          @error="handleImageError"
                        />
                      </div>
                    </template>
                    <template v-else>
                      <!-- Para otros partners, usar <a> normal -->
                      <a 
                        :href="partner.url" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        :data-tooltip-target="`tooltip-${partner.partner}-sale`"
                        data-tooltip-placement="bottom"
                      >
                        <img 
                          :src="getPartnerLogo(partner.partner)" 
                          :alt="`Logo de ${partner.partner}`"
                          class="h-auto w-auto object-contain cursor-pointer"
                          @error="handleImageError"
                        />
                      </a>
                    </template>
                    
                    <!-- Tooltip de Flowbite -->
                    <div 
                      :id="`tooltip-${partner.partner}-sale`" 
                      role="tooltip" 
                      class="absolute z-10 text-center invisible inline-block px-3 py-2 text-xs font-medium text-white bg-gray-900 rounded-lg shadow-sm opacity-0 tooltip dark:bg-gray-700"
                    >
                      <template v-if="partner.partner === 'Clasificados'">
                        Debes ir a Clasificados para buscar <br> el anuncio. El enlace no está <br> disponible en Tuin
                      </template>
                      <template v-else>
                        Da click para  <br> ver la propiedad
                      </template>
                      <div class="tooltip-arrow" data-popper-arrow></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Fila para portales de renta -->
          <div v-if="rentalPortals && rentalPortals.length > 0" class="mb-6">
            <div class="mb-3">
              <p class="text-sm font-normal text-gray-900">
                Encuentra tu propiedad en <span class="font-semibold text-tuin-100">renta</span> en otras plataformas
              </p>
            </div>
            <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2">
              <div 
                v-for="partner in rentalPortals" 
                :key="`rental-${partner.partner}`"
                class="flex items-start justify-start p-2"
              >
                <div class="flex flex-col items-start">
                  <div class="flex justify-start">
                    <!-- Logo del partner con tooltip -->
                    <template v-if="partner.partner === 'Clasificados'">
                      <!-- Para Clasificados, usar div clickeable en lugar de <a> -->
                      <div 
                        @click="handlePartnerClick(partner, $event)"
                        :data-tooltip-target="`tooltip-${partner.partner}-rental`"
                        data-tooltip-placement="bottom"
                        class="cursor-pointer"
                      >
                        <img 
                          :src="getPartnerLogo(partner.partner)" 
                          :alt="`Logo de ${partner.partner}`"
                          class="h-auto w-auto object-contain cursor-pointer"
                          @error="handleImageError"
                        />
                      </div>
                    </template>
                    <template v-else>
                      <!-- Para otros partners, usar <a> normal -->
                      <a 
                        :href="partner.url" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        :data-tooltip-target="`tooltip-${partner.partner}-rental`"
                        data-tooltip-placement="bottom"
                      >
                        <img 
                          :src="getPartnerLogo(partner.partner)" 
                          :alt="`Logo de ${partner.partner}`"
                          class="h-auto w-auto object-contain cursor-pointer"
                          @error="handleImageError"
                        />
                      </a>
                    </template>
                    
                    <!-- Tooltip de Flowbite -->
                    <div 
                      :id="`tooltip-${partner.partner}-rental`" 
                      role="tooltip" 
                      class="absolute z-10 text-center invisible inline-block px-3 py-2 text-xs font-medium text-white bg-gray-900 rounded-lg shadow-sm opacity-0 tooltip dark:bg-gray-700"
                    >
                      <template v-if="partner.partner === 'Clasificados'">
                        Debes ir a Clasificados para buscar <br> el anuncio. El enlace no está <br> disponible en Tuin
                      </template>
                      <template v-else>
                        Da click para  <br> ver la propiedad
                      </template>
                      <div class="tooltip-arrow" data-popper-arrow></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
        <template v-else>
          <div class="mb-3">
            <p class="text-sm font-normal text-gray-900">Encuentra tu propiedad en otras plataformas</p>
          </div>
          <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2">
            <div 
              v-for="partner in otherPortals" 
              :key="partner.partner"
              class="flex items-start justify-start p-2"
            >
              <div class="flex flex-col items-start">
                <div class="flex justify-start">
                  <!-- Logo del partner con tooltip -->
                  <template v-if="partner.partner === 'Clasificados'">
                    <!-- Para Clasificados, usar div clickeable en lugar de <a> -->
                    <div 
                      @click="handlePartnerClick(partner, $event)"
                      :data-tooltip-target="`tooltip-${partner.partner}-other`"
                      data-tooltip-placement="bottom"
                      class="cursor-pointer"
                    >
                      <img 
                        :src="getPartnerLogo(partner.partner)" 
                        :alt="`Logo de ${partner.partner}`"
                        class="h-auto w-auto object-contain cursor-pointer"
                        @error="handleImageError"
                      />
                    </div>
                  </template>
                  <template v-else>
                    <!-- Para otros partners, usar <a> normal -->
                    <a 
                      :href="partner.url" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      :data-tooltip-target="`tooltip-${partner.partner}-other`"
                      data-tooltip-placement="bottom"
                    >
                      <img 
                        :src="getPartnerLogo(partner.partner)" 
                        :alt="`Logo de ${partner.partner}`"
                        class="h-auto w-auto object-contain cursor-pointer"
                        @error="handleImageError"
                      />
                    </a>
                  </template>
                  
                  <!-- Tooltip de Flowbite -->
                  <div 
                    :id="`tooltip-${partner.partner}-other`" 
                    role="tooltip" 
                    class="absolute z-10 text-center invisible inline-block px-3 py-2 text-xs font-medium text-white bg-gray-900 rounded-lg shadow-sm opacity-0 tooltip dark:bg-gray-700"
                  >
                    <template v-if="partner.partner === 'Clasificados'">
                      Debes ir a Clasificados para buscar <br> el anuncio. El enlace no está <br> disponible en Tuin
                    </template>
                    <template v-else>
                      Da click para  <br> ver la propiedad
                    </template>
                    <div class="tooltip-arrow" data-popper-arrow></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
    
    <!-- Estado vacío -->
    <div v-else class="flex flex-col items-center justify-center text-center py-8">
      <div class="flex justify-center items-center mb-4">
        <svg xmlns="http://www.w3.org/2000/svg" width="153" height="146" viewBox="0 0 153 146" fill="none" class="mx-auto">
        <g clip-path="url(#clip0_3853_92457)">
          <path d="M117.054 146L118.109 126.405C118.109 126.405 152.704 119.241 152.704 98.5917C152.704 77.9427 96.1708 75.4143 90.4753 84.8959C90.4184 84.9908 90.3618 85.0856 90.3055 85.1803C83.2707 97.0308 87.2425 112.416 99.2133 119.259C103.307 121.599 108.187 123.602 113.89 124.719L113.488 145.536" fill="#CCCCCC"/>
          <path d="M84.9677 39.0159C95.7539 39.0159 104.498 30.2819 104.498 19.508C104.498 8.73401 95.7539 0 84.9677 0C74.1815 0 65.4375 8.73401 65.4375 19.508C65.4375 30.2819 74.1815 39.0159 84.9677 39.0159Z" fill="white"/>
          <path d="M56.1565 141.355L34.1298 139.952L32.942 41.0189C32.9133 38.6176 33.8717 36.2662 35.5719 34.568L59.1695 10.9972C62.4729 7.69799 67.6721 7.45955 71.2637 10.444L96.8723 31.7226C98.9615 33.4585 100.141 36.0109 100.109 38.7251L99.2294 113.128L110.36 128.569L110.383 128.602L112.269 144.928L56.1565 141.355Z" fill="#E6E6E6"/>
          <path d="M78.6562 41.623V37.0354C78.6562 35.8245 79.6426 34.8393 80.8549 34.8393H87.3079C88.5202 34.8393 89.5064 35.8245 89.5064 37.0354V41.623C89.5064 42.8339 88.5202 43.8192 87.3079 43.8192H80.8549C79.6426 43.8192 78.6562 42.834 78.6562 41.623Z" fill="white"/>
          <path d="M67.811 109.374C66.6563 109.333 65.7252 108.397 65.691 107.244L65.208 90.8381C65.1874 90.1378 65.4909 89.487 66.0407 89.0519C66.5905 88.617 67.2945 88.4719 67.9723 88.6515L68.4553 88.7803C69.4162 89.0365 70.0874 89.9089 70.0874 90.9022V107.179C70.0874 107.781 69.8488 108.342 69.4158 108.759C69.0019 109.159 68.4632 109.375 67.891 109.375C67.8644 109.375 67.8377 109.375 67.811 109.374Z" fill="#737373"/>
          <path d="M47.7695 107.11V90.0876C47.7695 88.8859 48.7118 87.9216 49.9146 87.8919L58.5972 87.6805C59.8008 87.6639 60.8134 88.6074 60.8487 89.8124L61.3488 107.047C61.3661 107.645 61.1457 108.211 60.7279 108.641C60.3104 109.07 59.7504 109.306 59.1511 109.306H49.9682C48.7559 109.306 47.7695 108.321 47.7695 107.11Z" fill="#737373"/>
          <path d="M99.0887 118.743L105.894 130.827L106.675 144.418L112.094 144.761L110.234 128.66L99.0739 113.177C98.1107 114.909 98.1163 117.016 99.0887 118.743Z" fill="#CCCCCC"/>
          <path d="M106.458 140.528L84.3711 140.83L84.3753 141.139L106.463 140.838L106.458 140.528Z" fill="#737373"/>
          <path d="M105.118 137.795L84.1758 137.959L84.1782 138.268L105.12 138.105L105.118 137.795Z" fill="#737373"/>
          <path d="M104.032 135.002L84 135.393L84.0061 135.703L104.038 135.312L104.032 135.002Z" fill="#737373"/>
          <path d="M103.409 132.225L84.1719 132.863L84.1822 133.173L103.42 132.535L103.409 132.225Z" fill="#737373"/>
          <path d="M102.479 129.752L83.9961 130.401L84.007 130.711L102.49 130.062L102.479 129.752Z" fill="#737373"/>
          <path d="M101.244 127.576H83.2422V127.886H101.244V127.576Z" fill="#737373"/>
          <path d="M100.312 125.403L82.8164 125.608L82.82 125.917L100.315 125.713L100.312 125.403Z" fill="#737373"/>
          <path d="M99.3785 123.237L82.4258 123.8L82.4361 124.109L99.3888 123.547L99.3785 123.237Z" fill="#737373"/>
          <path d="M98.76 121.081L82.0195 121.375L82.025 121.685L98.7655 121.391L98.76 121.081Z" fill="#737373"/>
          <path d="M97.5173 118.936L81.4883 119.437L81.498 119.746L97.527 119.246L97.5173 118.936Z" fill="#737373"/>
          <path d="M96.9033 117.048L81.0156 117.203L81.0187 117.512L96.9063 117.357L96.9033 117.048Z" fill="#737373"/>
          <path d="M79.8555 110.671V89.6724C79.8555 88.4613 80.8418 87.4761 82.0541 87.4761H92.2271C93.4395 87.4761 94.4256 88.4613 94.4256 89.6724V110.671C94.4256 111.882 93.4395 112.867 92.2271 112.867H82.0541C80.8418 112.867 79.8555 111.882 79.8555 110.671Z" fill="white"/>
          <path d="M56.1573 141.355L38.1491 140.209L38.0039 140.2V126.279C38.0039 124.518 39.4378 123.086 41.2004 123.086H58.9877C60.7502 123.086 62.1842 124.518 62.1842 126.279V141.355H62.0292L56.1573 141.355Z" fill="#CCCCCC"/>
          <path d="M81.0938 72.5845V56.5397C81.0938 55.3288 82.0801 54.3436 83.2924 54.3436H86.6453C87.8577 54.3436 88.8438 55.3288 88.8438 56.5397V72.5845C88.8438 73.7953 87.8577 74.7805 86.6453 74.7805H83.2924C82.0801 74.7805 81.0938 73.7953 81.0938 72.5845Z" fill="#737373"/>
          <path d="M84.5025 142.903L83.5725 129.279L80.7824 116.274L80.0074 112.713C78.4148 111.631 76.2702 112.834 76.3664 114.755L76.4424 116.274L79.2324 129.279L80.1624 142.903" fill="#CCCCCC"/>
          <path d="M64.033 51.5228V66.4035C64.033 67.7195 62.883 68.7901 61.4695 68.7901H45.4854C44.0718 68.7901 42.9219 67.7195 42.9219 66.4035V51.5228C42.9219 50.2068 44.0718 49.1362 45.4854 49.1362H61.4695C62.883 49.1362 64.033 50.2068 64.033 51.5228ZM60.4779 68.5093C62.2748 68.5093 63.7314 67.1532 63.7314 65.4804V58.9013C63.7314 53.6632 59.1703 49.417 53.5439 49.417H45.4854C44.2362 49.417 43.2235 50.3598 43.2235 51.5228V64.3273C43.2235 66.637 45.2346 68.5093 47.7155 68.5093L60.4779 68.5093Z" fill="#737373"/>
          <path d="M53.9446 67.3862H48.7866C48.6566 67.3862 48.5508 67.0713 48.5508 66.6842C48.5508 66.2972 48.6566 65.9824 48.7866 65.9824H53.9446C54.0747 65.9824 54.1804 66.2972 54.1804 66.6842C54.1804 67.0713 54.0747 67.3862 53.9446 67.3862Z" fill="white"/>
        </g>
        <defs>
          <clipPath id="clip0_3853_92457">
            <rect width="152" height="146" fill="white" transform="translate(0.703125)"/>
          </clipPath>
        </defs>
      </svg>
      </div>
      <p class="mt-1 text-sm text-gray-900 sm:w-96">
        {{ getEmptyStateDescription() }}
      </p>
    </div>
    
    <!-- Modal de Clasificados -->
    <ClasificadosModal 
      v-if="showClasificadosModal" 
      @close="showClasificadosModal = false"
    />
    
  </div>
</template>

<script setup>
import { onMounted, computed, nextTick, watch, ref } from 'vue'
import { initTooltips } from 'flowbite'
import ClasificadosModal from '@/components/properties/modal/clasificadosModal.vue'
// Importar las imágenes directamente
import easybrokerLogo from '@/assets/imgs/portals/easybroker-logo.svg'
import pincaliLogo from '@/assets/imgs/portals/pincali-logo.svg'
import propiedadesLogo from '@/assets/imgs/portals/propiedades-logo.svg'
import tuinLogo from '@/assets/imgs/portals/LogoTuinv2.svg'
import tuinasesoresLogo from '@/assets/imgs/portals/tuinasesores-logo.svg'
import clasificadosLogo from '@/assets/imgs/portals/clasificados-logo.svg'
import defaultLogo from '@/assets/imgs/default_company.png'

const props = defineProps({
  partners: {
    type: Array,
    default: () => []
  },
  slug_url: {
    type: String,
    default: ''
  },
  property_id: {
    type: [String, Number],
    default: null
  },
  has_commission_sale: {
    type: [Number, String, Boolean],
    default: 0
  },
  has_commission_rent: {
    type: [Number, String, Boolean],
    default: 0
  },
  status_id: {
    type: [Number, String],
    default: null
  },
  type_movement_id: {
    type: [Number, String],
    default: null
  }
})

// Variable reactiva para controlar el modal de Clasificados
const showClasificadosModal = ref(false)

// Computed property para incluir el portal de Tuin por defecto
const allPartners = computed(() => {
  //console.log('Creando allPartners con slug_url:', props.slug_url);
  
  const tuinPortal = {
    external_id: 'tuin-default',
    partner: 'Tuin',
    status: 'active',
    transaction_type: 'portal',
    url: props.slug_url || 'https://tuin.com' // URL por defecto si no hay slug_url
  }

  // Array inicial con solo Tuin
  let portals = [tuinPortal];

  // Solo agregar TuinAsesores si tiene comisión de venta o renta
  const hasCommission = Number(props.has_commission_sale) === 1 || Number(props.has_commission_rent) === 1;
  
  if (hasCommission) {
    const tuinAsesores = {
      external_id: 'tuin-asesores',
      partner: 'TuinAsesores',
      status: 'active',
      transaction_type: 'portal',
      url: props.property_id ? `/propiedad/${props.property_id}` : null
    }
    portals.push(tuinAsesores);
  }
  
  //console.log('Portal Tuin creado:', tuinPortal);
  //console.log('¿Tiene comisión?', hasCommission);
  
  // Filtrar partners solo con status 'active' antes de agregarlos
  const activePartners = props.partners.filter(partner => partner.status === 'active');
  
  // Verificar si EasyBroker está presente en los partners activos
  const hasEasyBroker = activePartners.some(partner => partner.partner === 'EasyBroker');
  
  // Verificar si han pasado 24 horas desde created_at del partner EasyBroker
  const has24HoursPassed = () => {
    const easyBrokerPartner = activePartners.find(partner => partner.partner === 'EasyBroker');
    if (!easyBrokerPartner || !easyBrokerPartner.created_at) return false;
    
    const createdDate = new Date(easyBrokerPartner.created_at);
    const currentDate = new Date();
    const timeDifference = currentDate - createdDate;
    const hoursDifference = timeDifference / (1000 * 60 * 60); // Convertir a horas
    
    return hoursDifference >= 24;
  };
  
  // Agregar primero todos los portals principales y partners activos
  let allPortals = [...portals, ...activePartners];
  
  // Si EasyBroker está presente Y han pasado 24 horas, agregar Clasificados al final
  if (hasEasyBroker && has24HoursPassed()) {
    const clasificados = {
      external_id: 'clasificados-portal',
      partner: 'Clasificados',
      status: 'active',
      transaction_type: 'portal',
      url: null // No URL, se maneja con modal
    }
    allPortals.push(clasificados);
  }
  
  // Retornar todos los portales con Clasificados al final
  return allPortals
})

// Computed para separar portales de Tuin de otros portales
const tuinPortals = computed(() => {
  return allPartners.value.filter(partner => 
    partner.partner === 'Tuin' || partner.partner === 'TuinAsesores'
  )
})

const otherPortals = computed(() => {
  return allPartners.value.filter(partner => 
    partner.partner !== 'Tuin' && partner.partner !== 'TuinAsesores'
  )
})

// Computed para filtrar portales de venta cuando type_movement_id === 3
const salePortals = computed(() => {
  if (Number(props.type_movement_id) !== 3) return []
  
  return otherPortals.value.filter(partner => {
    switch (partner.partner) {
      case 'EasyBroker':
        return partner.transaction_type === 'sale'
      case 'EasyAviso':
        return partner.transaction_type === 'sale'
      case 'Pincali':
        return partner.transaction_type === 'sale'
      case 'PropiedadesCom':
        return ['sale-rent', 'sale'].includes(partner.transaction_type)
      case 'Clasificados':
        return true // Clasificados siempre se muestra
      default:
        return true
    }
  })
})

// Computed para filtrar portales de renta cuando type_movement_id === 3
const rentalPortals = computed(() => {
  if (Number(props.type_movement_id) !== 3) return []
  
  return otherPortals.value.filter(partner => {
    switch (partner.partner) {
      case 'EasyBroker':
        return partner.transaction_type === 'rental'
      case 'EasyAviso':
        return partner.transaction_type === 'rental'
      case 'Pincali':
        return partner.transaction_type === 'rental'
      case 'PropiedadesCom':
        return partner.transaction_type === 'rent'
      case 'Clasificados':
        return true // Clasificados siempre se muestra
      default:
        return true
    }
  })
})

// Función para manejar el clic en partners
const handlePartnerClick = (partner, event) => {
  if (partner.partner === 'Clasificados') {
    event.preventDefault()
    showClasificadosModal.value = true
  }
  // Para otros partners, el enlace <a> manejará la navegación normalmente
}

// Función para obtener la ruta del logo según el partner
const getPartnerLogo = (partnerName) => {
  const logos = {
    'EasyBroker': easybrokerLogo,
    'EasyAviso': pincaliLogo,
    'Pincali': pincaliLogo,
    'PropiedadesCom': propiedadesLogo,
    'Clasificados': clasificadosLogo,
    'Tuin': tuinLogo,
    'TuinAsesores': tuinasesoresLogo,
  }
  
  // Retorna el logo específico o un logo por defecto
  return logos[partnerName] || defaultLogo
}

// Función para manejar errores de imagen
const handleImageError = (event) => {
  // Si la imagen no se puede cargar, usa un logo por defecto
  event.target.src = defaultLogo
  console.warn('Error cargando logo del partner:', event.target.alt)
}

// Función para obtener la descripción del estado vacío según status_id
const getEmptyStateDescription = () => {
  const statusId = props.status_id
  
  if (statusId === null || statusId === undefined) {
    return 'La información de tu propiedad está en borrador. Termina de completarla para publicarla.'
  } else if (Number(statusId) === 2) {
    return 'Tu propiedad no está visible porque está desactivada. Actívala para que aparezca en los demás portales.'
  } else {
    return 'Los portales no están disponibles para esta propiedad.'
  }
}

// Watcher para reinicializar tooltips cuando cambien los partners
watch([allPartners, salePortals, rentalPortals], async () => {
  await nextTick()
  initTooltips()
}, { deep: true })

onMounted(async () => {
  //console.log('Partners cargados:', props.partners);
  //console.log('Slug URL:', props.slug_url);
  //console.log('Todos los portales (incluye Tuin):', allPartners.value);
  
  // Inicializar tooltips de Flowbite después de que el DOM se actualice
  await nextTick()
  initTooltips()
})
</script>

<style scoped>

</style>
